import { useRef, type ChangeEvent } from "react";

export interface ImageUploadPreviewProps {
	label: string;
	value?: File | null;
	preview: string | null;
	onChange: (file: File | null, preview: string | null) => void;
	error?: string;
	maxSizeMB?: number;
}

const validateFile = (file: File, maxSizeMB: number): string | null => {
	const maxSize = maxSizeMB * 1024 * 1024;
	const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

	if (!allowedTypes.includes(file.type)) {
		return "Please upload a valid image file (JPEG, PNG, GIF, or WebP)";
	}
	if (file.size > maxSize) {
		return `Image must be less than ${maxSizeMB}MB`;
	}
	return null;
};

export default function ImageUploadPreview({ label, preview, onChange, error, maxSizeMB = 5 }: ImageUploadPreviewProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const validationError = validateFile(file, maxSizeMB);
			if (validationError) {
				onChange(null, null);
				return;
			}

			// Revoke previous preview URL if exists
			if (preview) {
				URL.revokeObjectURL(preview);
			}

			const previewUrl = URL.createObjectURL(file);
			onChange(file, previewUrl);
		}
	};

	const handleRemove = () => {
		if (preview) {
			URL.revokeObjectURL(preview);
		}
		onChange(null, null);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	return (
		<div>
			<label className="block text-sm font-medium text-primary mb-1.5">{label}</label>
			<input ref={inputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

			{preview ? (
				<div className="relative inline-block">
					<img src={preview} alt="Preview" className="w-20 h-20 rounded-full object-cover border border-gray-200" />
					<button
						type="button"
						onClick={handleRemove}
						className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 transition-colors"
						aria-label="Remove image"
					>
						&times;
					</button>
				</div>
			) : (
				<button
					type="button"
					onClick={() => inputRef.current?.click()}
					className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-secondary hover:bg-gray-50 transition-colors"
				>
					Upload Image
				</button>
			)}

			{error && <p className="text-xs text-red-500 mt-1">{error}</p>}
		</div>
	);
}
